import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ssoSettingsService } from "@/services/sso-settings-service";
import {
  ConfigureAppleProviderRequest,
  ConfigureGoogleProviderRequest,
  ConfigureProviderResponse,
  RemoveProviderResponse,
  SsoProviderAlias,
  SsoSettingsResponse,
} from "@/types/sso";

export const ssoSettingsKeys = {
  all: ["ssoSettings"] as const,
  settings: (tenantId: string) =>
    [...ssoSettingsKeys.all, "settings", tenantId] as const,
};

export const useGetSsoSettings = (tenantId: string, enabled: boolean) => {
  return useQuery<SsoSettingsResponse>({
    queryKey: ssoSettingsKeys.settings(tenantId),
    queryFn: () => ssoSettingsService.getSettings(tenantId),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!tenantId,
  });
};

export const useConfigureGoogleSso = (tenantId: string) => {
  const qc = useQueryClient();

  return useMutation<
    ConfigureProviderResponse,
    unknown,
    ConfigureGoogleProviderRequest
  >({
    mutationFn: (payload) =>
      ssoSettingsService.configureGoogle(tenantId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ssoSettingsKeys.settings(tenantId) });
    },
  });
};

export const useConfigureAppleSso = (tenantId: string) => {
  const qc = useQueryClient();

  return useMutation<
    ConfigureProviderResponse,
    unknown,
    ConfigureAppleProviderRequest
  >({
    mutationFn: (payload) =>
      ssoSettingsService.configureApple(tenantId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ssoSettingsKeys.settings(tenantId) });
    },
  });
};

export const useRemoveSsoProvider = (tenantId: string) => {
  const qc = useQueryClient();

  return useMutation<RemoveProviderResponse, unknown, SsoProviderAlias>({
    mutationFn: (provider) =>
      ssoSettingsService.removeProvider(tenantId, provider),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ssoSettingsKeys.settings(tenantId) });
    },
  });
};
